/**
 * Reiter *Stammdaten* für eine Einheit (Spec 02a, „Reiter Stammdaten").
 *
 * Struktur und Verhalten spiegeln `BuildingMasterData` — nur die Felder
 * unterscheiden sich. `usage_type` ist die einzige Auswahl unter den drei
 * Arten; sie kommt über `UsageTypeField` von `GET /units/usage-types`.
 */

import type { UseQueryResult } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { ArchiveControls } from '@/components/ArchiveControls'
import { ArchivedBanner } from '@/components/ArchivedBanner'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { areaToInputValue, parseAreaInput } from '@/features/properties/area'
import type { UnitDetail, UsageTypeValue } from '@/features/properties/api'
import { useArchivePreview, useArchiveUnit, useUnarchiveUnit, useUpdateUnit } from '@/features/properties/api'
import { FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { integerToInputValue, parseIntegerInput } from '@/features/properties/integer'
import { UsageTypeField } from '@/features/properties/UsageTypeField'

interface UnitMasterDataProps {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  /** `ObjectHead.archived_by_id`/`archived_by_label` (Spec 02b) — kommen von
   * `routes/ObjectDetail.tsx`, nicht von `UnitDetail` (I9). */
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly query: UseQueryResult<UnitDetail>
}

interface FormValues {
  readonly unitRef: string
  readonly usageType: string
  readonly areaSqm: string
  readonly rooms: string
  readonly floor: string
}

function deriveForm(unit: UnitDetail): FormValues {
  return {
    unitRef: unit.unit_ref ?? '',
    usageType: unit.usage_type.value,
    areaSqm: unit.area_sqm === null ? '' : areaToInputValue(unit.area_sqm),
    rooms: unit.rooms === null ? '' : areaToInputValue(unit.rooms),
    floor: unit.floor === null ? '' : integerToInputValue(unit.floor),
  }
}

function UnitReadField({ label, value, testId }: { readonly label: string; readonly value: string | null; readonly testId: string }) {
  return <div><dt className="text-label text-muted">{label}</dt><dd data-testid={testId} className="text-body text-text">{value ?? '—'}</dd></div>
}

export function UnitMasterData({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  query,
}: UnitMasterDataProps) {
  return (
    <section data-testid="unit-master-data" className="px-(--page-x) py-4">
      {query.isPending && <SkeletonRows testId="unit-master-data-loading" rows={5} />}

      {query.isError && (
        <ErrorState
          testId="unit-master-data-error"
          retryTestId="unit-master-data-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && (
        <UnitMasterDataForm
          entityId={entityId}
          canEdit={canEdit}
          canUnarchive={canUnarchive}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          unit={query.data}
        />
      )}
    </section>
  )
}

function UnitMasterDataForm({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  unit,
}: {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly unit: UnitDetail
}) {
  const updateMutation = useUpdateUnit()
  const archiveMutation = useArchiveUnit()
  const unarchiveMutation = useUnarchiveUnit()
  const archivePreviewMutation = useArchivePreview()

  const [form, setForm] = useState<FormValues>(() => deriveForm(unit))
  const [initial, setInitial] = useState<FormValues>(() => deriveForm(unit))
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [archiveError, setArchiveError] = useState<string | undefined>(undefined)
  const editTriggerRef = useRef<HTMLButtonElement>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  const [syncedKey, setSyncedKey] = useState(`${unit.id}:${String(unit.version)}`)
  const currentKey = `${unit.id}:${String(unit.version)}`
  if (currentKey !== syncedKey) {
    setSyncedKey(currentKey)
    const next = deriveForm(unit)
    setForm(next)
    setInitial(next)
  }

  const isArchived = unit.archived_at !== null
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial)

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function openEditor() {
    const next = deriveForm(unit)
    setForm(next)
    setInitial(next)
    setFieldError(undefined)
    setGeneralError(undefined)
    setIsEditOpen(true)
  }

  function handleEditOpenChange(open: boolean) {
    if (open) openEditor()
    else setIsEditOpen(false)
  }

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await updateMutation.mutateAsync({
        unitId: entityId,
        ifMatch: String(unit.version),
        body: {
          unit_ref: form.unitRef,
          usage_type: form.usageType as UsageTypeValue,
          area_sqm: parseAreaInput(form.areaSqm),
          rooms: parseAreaInput(form.rooms),
          floor: parseIntegerInput(form.floor),
        },
      })
      setIsEditOpen(false)
      setConfirmation('Änderungen gespeichert')
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleUnarchive() {
    setArchiveError(undefined)
    try {
      await unarchiveMutation.mutateAsync({ unitId: entityId, ifMatch: String(unit.version) })
    } catch (error) {
      setArchiveError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <dl data-testid="unit-master-data-read" className="grid gap-3 sm:grid-cols-2">
          <UnitReadField label="Bezeichnung" value={unit.unit_ref} testId="unit-read-unit-ref" />
          <UnitReadField label="Nutzungsart" value={unit.usage_type.label} testId="unit-read-usage-type" />
          <UnitReadField label="Fläche" value={unit.area_sqm_display} testId="unit-read-area-sqm" />
          <UnitReadField label="Zimmer" value={unit.rooms_display} testId="unit-read-rooms" />
          <UnitReadField label="Etage" value={unit.floor_display} testId="unit-read-floor" />
        </dl>
        {canEdit && !isArchived && (
          <button type="button" ref={editTriggerRef} data-testid="unit-edit-open" onClick={openEditor} className="border-border text-text border px-3">
            Bearbeiten
          </button>
        )}
      </div>
      {isArchived ? (
        <ArchivedBanner
          kind="unit"
          canUnarchive={canUnarchive}
          parentId={unit.parent_id}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          onUnarchive={() => void handleUnarchive()}
          isUnarchiving={unarchiveMutation.isPending}
        />
      ) : (
        <ArchiveControls
          kind="unit"
          entityId={entityId}
          label={unit.label}
          version={unit.version}
          parentId={unit.parent_id}
          isArchiving={archiveMutation.isPending}
          onArchive={(ifMatch) => archiveMutation.mutateAsync({ unitId: entityId, ifMatch })}
          onError={setArchiveError}
          archivePreview={archivePreviewMutation}
        />
      )}
      {archiveError !== undefined && <p data-testid="unit-archive-error" role="alert" className="text-label text-status-overdue">{archiveError}</p>}
      <MasterDataEditSheet open={isEditOpen} onOpenChange={handleEditOpenChange} title="Einheit bearbeiten"
        triggerRef={editTriggerRef}
        formId="unit-edit-form"
        saveTestId="unit-edit-save"
        saveDisabled={!isDirty || updateMutation.isPending}
        isSaving={updateMutation.isPending}
      >
    <form id="unit-edit-form" onSubmit={(event) => void handleSave(event)} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
      <FormField
        label="Bezeichnung"
        testId="unit-edit-unit-ref"
        apiField="unit_ref"
        errorPrefix="unit-edit"
        required
        disabled={isArchived}
        value={form.unitRef}
        onChange={updateField('unitRef')}
        error={fieldError}
      />
      <UsageTypeField
        testId="unit-edit-usage-type"
        errorPrefix="unit-edit"
        disabled={isArchived}
        value={form.usageType}
        onChange={updateField('usageType')}
        error={fieldError}
      />
      <FormField
        label="Fläche (m²)"
        testId="unit-edit-area-sqm"
        apiField="area_sqm"
        errorPrefix="unit-edit"
        inputMode="decimal"
        disabled={isArchived}
        value={form.areaSqm}
        onChange={updateField('areaSqm')}
        error={fieldError}
      />
      <FormField
        label="Zimmer"
        testId="unit-edit-rooms"
        apiField="rooms"
        errorPrefix="unit-edit"
        inputMode="decimal"
        disabled={isArchived}
        value={form.rooms}
        onChange={updateField('rooms')}
        error={fieldError}
      />
      <FormField
        label="Etage"
        testId="unit-edit-floor"
        apiField="floor"
        errorPrefix="unit-edit"
        inputMode="numeric"
        disabled={isArchived}
        value={form.floor}
        onChange={updateField('floor')}
        error={fieldError}
      />

      {generalError !== undefined && (
        <p data-testid="unit-edit-error" role="alert" className="text-label text-status-overdue">
          {generalError}
        </p>
      )}

    </form>
      </MasterDataEditSheet>
      <ConfirmationStrip message={confirmation} onDismiss={() => { setConfirmation(null) }} testId="object-save-confirmation" />
    </>
  )
}
